// One tide pool and one simultaneous turn. resolveTurn() computes the whole
// turn up front: shells fly, wakes are painted, pens close, the undertow
// spreads. The renderer only replays `frames` and `events`.
// Tonight's rules (rules.js) and each crab's shell (shells.js) change the
// numbers here; nothing outside the sim decides an outcome.
import { ONE, idiv, isqrt, len, withLength, clampi } from './num.js';
import {
  makeGrid, cellAt, cellsInDisc, forDisc, markSolid, enclose, spreadDark, flood, darkCells,
  passable, centreOf, insidePoly, OPEN, LIT, OUT, SOLID, CELL,
} from './grid.js';
import { TUNE } from './content.js';
import { traitParams } from './shells.js';

export const TICKS_PER_SECOND = 120;
const T = TUNE;

// What each kind of target does. Little ones swim toward the dark and are
// saved in lit water; stars are reflections (no body) lit by pens; lanterns
// drift toward the sea outlet; glass floats are pushed into nets.
export const TARGETS = {
  fry: { swim: 'dark', body: true, save: 'lit', mass: 1 },
  takoko: { swim: 'dark', body: true, save: 'lit', mass: 1 },
  seahorse: { swim: 'dark', body: true, save: 'lit', mass: 1 },
  fugu: { swim: 'dark', body: true, save: 'lit', mass: 1 },
  star: { swim: null, body: false, save: 'smallPen', mass: 1 },
  lantern: { swim: 'exit', body: true, save: 'exit', mass: 1 },
  float: { swim: null, body: true, save: 'net', mass: 2 },
};
export const targetOf = l => TARGETS[l.kind] || TARGETS.fry;

// ---------------------------------------------------------------- pool setup

export function makePool({
  basin, poly, crabs, littles, rocks = [], anemones = [], kelps = [], seeps, boss = null, spread, seepRadius,
  rules = {}, vortices = [], logs = [], rival = null, pearls = [],
  exit = null, nets = [], stencil = null, mode = 'pen',
}) {
  const g = makeGrid(poly);
  for (const o of [...rocks, ...anemones]) markSolid(g, o.x, o.y, o.r);
  if (boss) markSolid(g, boss.x, boss.y, boss.r);
  const pool = {
    basin, poly, grid: g, turn: 0, spread,
    crabs: crabs.map((c, k) => ({ id: k, type: 'crab', char: c.char, shell: c.shell || null, x: c.x, y: c.y, vx: 0, vy: 0, hugged: false, cool: 0 })),
    littles: littles.map((l, k) => ({ id: k, type: 'little', kind: l.kind, name: l.name, x: l.x, y: l.y, vx: 0, vy: 0, state: 'lost', cool: 0 })),
    rocks, anemones, kelps,
    boss: boss ? { ...boss, calm: false } : null,
    rules: { current: null, rain: false, fog: false, bloom: 1000, calm: false, swift: false, ward: false, ...rules },
    vortices,
    logs: logs.map((l, k) => ({ id: k, type: 'log', x: l.x, y: l.y, r: l.r, vx: 0, vy: 0, cool: 0 })),
    rival: rival ? { ...rival, axis: 0 } : null,
    pearls: pearls.map(q => ({ ...q, taken: false })),
    bossBonus: 0,
    mode, exit, nets,
    stencil: stencil ? { cells: stencil.cells, goal: stencil.goal, done: false } : null,
    seeps: [],
    stats: { saved: 0, taken: 0, pens: 0, allFour: 0, bestPen: 0, pearls: 0 },
  };
  // Dark blobs at the seeps, and around the boss (its ink).
  for (const [x, y] of seeps) for (const i of cellsInDisc(g, x, y, seepRadius)) if (g.kind[i] === OPEN) { g.dark[i] = 1; pool.seeps.push(i); }
  if (boss) for (const i of cellsInDisc(g, boss.x, boss.y, boss.r + T.bossInk)) if (g.kind[i] === OPEN) g.dark[i] = 1;
  placeLogs(pool);
  enclose(g, 0); // rock pockets the dark cannot reach start lit
  // Targets that would start in already-lit water are not part of tonight's pool.
  pool.littles = pool.littles.filter(l => { const i = cellAt(g, l.x, l.y); return i >= 0 && g.kind[i] !== LIT; }).map((l, k) => ({ ...l, id: k }));
  return pool;
}

// Driftwood is a wall where it lies at the start of a turn; when knocked, its
// wall follows it at the end of the turn.
function placeLogs(p) {
  const g = p.grid;
  for (let i = 0; i < g.logMark.length; i++) {
    if (!g.logMark[i]) continue;
    if (g.kind[i] === SOLID) g.kind[i] = g.logMark[i] === 2 ? LIT : OPEN;
    g.logMark[i] = 0;
  }
  for (const l of p.logs) {
    forDisc(g, l.x, l.y, l.r, i => {
      if (g.kind[i] !== OPEN && g.kind[i] !== LIT) return;
      g.logMark[i] = g.kind[i] === LIT ? 2 : 1;
      g.kind[i] = SOLID;
    });
  }
}

// Cells of water still connected to the boss (what has to be fenced in).
export function bossWater(p) {
  const g = p.grid, b = p.boss;
  const seeds = cellsInDisc(g, b.x, b.y, b.r + CELL * 2).filter(i => passable(g, i, p.turn));
  return flood(g, seeds, p.turn).order.length;
}
export const bossCalmLine = p => T.bossCalmPermille + (p.bossBonus || 0);

export const openCells = g => { let n = 0; for (const k of g.kind) if (k === OPEN || k === LIT) n++; return n; };
export const litCells = g => { let n = 0; for (const k of g.kind) if (k === LIT) n++; return n; };
// Picture nights: share of the stencil that is lit, and whether nothing more can change.
export function stencilProgress(p) {
  const s = p.stencil, g = p.grid;
  if (!s) return null;
  let lit = 0, open = 0;
  for (const i of s.cells) { if (g.kind[i] === LIT) lit++; else if (!g.dark[i] && g.kind[i] === OPEN) open++; }
  return { lit, open, total: s.cells.length, permille: Math.round((lit * 1000) / s.cells.length) };
}
// How close the night's goal is, beyond saves (for the bots): lanterns nearer
// the outlet, floats nearer a net, more of the picture lit. Higher is better.
export function progressScore(p) {
  let s = 0;
  const live = p.littles.filter(l => l.state === 'lost');
  if (p.exit && live.some(l => l.kind === 'lantern')) {
    const f = exitField(p.grid, p.exit, blockFrom(p));
    for (const l of live) if (l.kind === 'lantern') { const i = cellAt(p.grid, l.x, l.y); s -= i >= 0 && f[i] >= 0 ? f[i] * 4 : 400; }
  }
  for (const l of live) if (l.kind === 'float' && p.nets.length) {
    const d = Math.min(...p.nets.map(n => isqrt((l.x - n.x) ** 2 + (l.y - n.y) ** 2)));
    s -= idiv(d * 4, CELL);
  }
  if (p.stencil) s += stencilProgress(p).lit * 6;
  return s;
}

export const poolDone = p => {
  if (!p.littles.every(l => l.state !== 'lost') || (p.boss && !p.boss.calm)) return false;
  if (!p.stencil) return true;
  const sp = stencilProgress(p);
  // Done when the picture is lit enough, or can no longer get there.
  return sp.permille >= p.stencil.goal || (sp.lit + sp.open) * 1000 < p.stencil.goal * sp.total;
};

// The undertow quickens the longer the crabs stay in one pool.
// 灯の加護 (lanterns) holds the first spread of each pool back by one ring.
export const spreadFor = p => Math.max(0, p.spread + Math.floor(p.turn / T.quickenEvery) + (p.boss && !p.boss.calm ? T.bossExtraSpread : 0) + (p.rules?.swift ? 1 : 0) - (p.rules?.ward && p.turn === 0 ? 1 : 0));
// Oldest wake stamp that still blocks during the coming turn (rain washes last turn's away).
export const blockFrom = p => (p.rules?.rain ? p.turn + 1 : p.turn);

// Cells the dark will reach at the end of this turn if no new wake is drawn
// (the telegraph shown while aiming). Uses the same walls as the real spread.
export function darkForecast(p) {
  const g = p.grid;
  const { order, dist } = flood(g, darkCells(g), blockFrom(p), spreadFor(p));
  return Array.from(order).filter(i => dist[i] > 0 && !g.dark[i]);
}

// ---------------------------------------------------------------- geometry

function rimSegments(poly) {
  // Signed area decides which side is inside (poly can wind either way).
  let a2 = 0;
  for (let i = 0; i < poly.length; i++) { const [x1, y1] = poly[i], [x2, y2] = poly[(i + 1) % poly.length]; a2 += x1 * y2 - x2 * y1; }
  const s = a2 > 0 ? 1 : -1;
  return poly.map((A, i) => {
    const B = poly[(i + 1) % poly.length];
    const dx = B[0] - A[0], dy = B[1] - A[1];
    return { ax: A[0], ay: A[1], dx, dy, l2: dx * dx + dy * dy, nx: -dy * s, ny: dx * s };
  });
}

// Push a body out of the segment and return the contact normal (length ONE,
// pointing into the pool) or null.
function segmentContact(b, r, seg) {
  const px = b.x - seg.ax, py = b.y - seg.ay;
  let t = px * seg.dx + py * seg.dy;
  let qx, qy;
  if (t <= 0) { qx = seg.ax; qy = seg.ay; }
  else if (t >= seg.l2) { qx = seg.ax + seg.dx; qy = seg.ay + seg.dy; }
  else { qx = seg.ax + idiv(seg.dx * t, seg.l2); qy = seg.ay + idiv(seg.dy * t, seg.l2); }
  let nx = b.x - qx, ny = b.y - qy;
  const d2 = nx * nx + ny * ny;
  if (d2 >= r * r) return null;
  // A centre behind the wall line (pushed through by a collision) goes back
  // to the inner side; at a reflex corner the neighbour segment handles it.
  const behind = nx * seg.nx + ny * seg.ny < 0;
  if (behind && (t <= 0 || t >= seg.l2)) return null;
  if (behind || d2 === 0) [nx, ny] = withLength(seg.nx, seg.ny, ONE);
  else [nx, ny] = withLength(nx, ny, ONE);
  b.x = qx + idiv(nx * r, ONE);
  b.y = qy + idiv(ny * r, ONE);
  return [nx, ny];
}

// Reflect velocity off a unit normal (length ONE) with restitution e (per mille).
function bounce(b, nx, ny, e) {
  const vn = idiv(b.vx * nx + b.vy * ny, ONE);
  if (vn >= 0) return 0;
  const k = idiv(vn * (1000 + e), 1000);
  b.vx -= idiv(k * nx, ONE);
  b.vy -= idiv(k * ny, ONE);
  return -vn;
}

function circleContact(b, r, o) {
  let nx = b.x - o.x, ny = b.y - o.y;
  const rr = r + o.r;
  const d2 = nx * nx + ny * ny;
  if (d2 >= rr * rr) return null;
  if (d2 === 0) { nx = 0; ny = ONE; } else [nx, ny] = withLength(nx, ny, ONE);
  b.x = o.x + idiv(nx * rr, ONE);
  b.y = o.y + idiv(ny * rr, ONE);
  return [nx, ny];
}

const radiusOf = b => (b.type === 'little' ? (b.kind === 'float' ? T.floatR : T.littleR) : b.type === 'log' ? b.r : T.crabR);
const massOf = b => (b.type === 'little' ? targetOf(b).mass : b.type === 'log' ? 6 : traitParams(b.shell).mass);
// Who an event is about, for the renderer.
const who = b => (b.type === 'crab' ? { crab: b.id } : b.type === 'little' ? { little: b.id } : { log: b.id });

// ---------------------------------------------------------------- the turn

export function launchVelocity(input) {
  const p = clampi(input.power | 0, 0, 1000);
  if (p < T.minPower) return [0, 0];
  const speed = T.minSpeed + idiv((T.maxSpeed - T.minSpeed) * p, 1000);
  return withLength(input.dx | 0, input.dy | 0, speed);
}

export function resolveTurn(source, inputs, { record = true } = {}) {
  const p = structuredClone(source);
  const g = p.grid;
  const R = p.rules;
  // This turn's wakes carry stamp S (plus each shell's extra life); stamps >= A still block.
  const S = p.turn + 1, A = blockFrom(p);
  const live = () => p.littles.filter(l => l.state === 'lost');
  const events = [];
  const frames = [];
  const segs = rimSegments(p.poly);
  const tps = p.crabs.map(c => traitParams(c.shell));

  // Launch.
  for (const c of p.crabs) {
    const input = inputs.find(i => i.crab === c.id);
    const [vx, vy] = input ? launchVelocity(input) : [0, 0];
    c.vx = vx; c.vy = vy; c.hugged = false; c.cool = 0;
    if (vx || vy) events.push({ type: 'launch', tick: 0, crab: c.id, x: c.x, y: c.y, speed: len(vx, vy) });
  }
  // Little ones swim toward the undertow; lanterns drift toward the sea outlet.
  const field = flood(g, darkCells(g), A).dist;
  const toExit = p.exit ? exitField(g, p.exit, A) : null;
  for (const l of live()) {
    const kind = targetOf(l).swim;
    const f = kind === 'dark' ? field : kind === 'exit' ? toExit : null;
    if (!f) continue;
    const i = cellAt(g, l.x, l.y);
    if (i < 0 || f[i] <= 0) continue;
    let best = i, bd = f[i];
    const cx = i % g.w;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const n = i + dx + dy * g.w;
      if ((dx || dy) && cx + dx >= 0 && cx + dx < g.w && f[n] >= 0 && f[n] < bd) { best = n; bd = f[n]; }
    }
    if (best === i) continue;
    const [bx, by] = centreOf(g, best), [ax, ay] = centreOf(g, i);
    [l.vx, l.vy] = withLength(bx - ax, by - ay, kind === 'exit' ? T.lanternSpeed : T.swimSpeed);
  }

  const lastFill = { tick: -99, pending: false, by: -1 };
  const recordFrame = () => {
    if (!record) return;
    const f = [];
    for (const c of p.crabs) f.push(c.x, c.y);
    for (const l of p.littles) f.push(l.x, l.y);
    for (const l of p.logs) f.push(l.x, l.y);
    frames.push(f);
  };
  const saveLittle = (l, tick, how) => {
    l.state = 'saved'; l.vx = 0; l.vy = 0;
    p.stats.saved++;
    events.push({ type: 'saved', tick, little: l.id, x: l.x, y: l.y, how });
  };

  const closePens = tick => {
    lastFill.tick = tick; lastFill.pending = false;
    const pen = enclose(g, A);
    if (pen.lit.length) {
      const saved = [];
      const fresh = new Set(pen.lit);
      for (const l of live()) {
        const save = targetOf(l).save;
        const i = cellAt(g, l.x, l.y);
        if (save === 'lit' && i >= 0 && g.kind[i] === LIT) { saved.push(l.id); saveLittle(l, tick, 'pen'); }
        // Stars are scooped only by small pens; a big flood of light washes them out.
        if (save === 'smallPen' && fresh.has(i)) {
          if (pen.lit.length <= T.starPenMax) { saved.push(l.id); saveLittle(l, tick, 'pen'); }
          else { l.state = 'faded'; events.push({ type: 'faded', tick, little: l.id, x: l.x, y: l.y }); }
        }
      }
      // Only pens that shelter someone count as pens for the record and the show.
      const allFour = pen.crabs === 0b1111 && saved.length > 0;
      if (saved.length) p.stats.pens++;
      if (allFour) p.stats.allFour++;
      p.stats.bestPen = Math.max(p.stats.bestPen, saved.length);
      // The crab whose wake closed the pen gives it its colour.
      const closer = lastFill.by >= 0 ? lastFill.by : Math.max(0, Math.log2(pen.crabs & -pen.crabs) | 0);
      for (const i of pen.lit) { g.pen[i] = pen.crabs; g.penBy[i] = 1 + closer; }
      events.push({ type: 'pen', tick, cells: pen.lit, crabs: pen.crabs, closer, saved, allFour });
    }
    if (p.boss && !p.boss.calm) checkBoss(tick);
    if (p.stencil && !p.stencil.done && stencilProgress(p).permille >= p.stencil.goal) {
      p.stencil.done = true;
      events.push({ type: 'picture', tick });
    }
  };

  const checkBoss = tick => {
    const b = p.boss;
    const seeds = cellsInDisc(g, b.x, b.y, b.r + CELL * 2).filter(i => passable(g, i, A));
    const { order } = flood(g, seeds, A);
    if (order.length * 1000 > openCells(g) * bossCalmLine(p)) return;
    b.calm = true;
    const cells = Array.from(order).filter(i => g.kind[i] === OPEN);
    for (const i of cells) { g.kind[i] = LIT; g.dark[i] = 0; g.pen[i] = 16; }
    const saved = [];
    for (const l of live()) {
      if (targetOf(l).save !== 'lit') continue;
      const i = cellAt(g, l.x, l.y);
      if (i >= 0 && g.kind[i] === LIT) { saved.push(l.id); saveLittle(l, tick, 'pen'); }
    }
    events.push({ type: 'calm', tick, cells, saved });
  };

  // Paint the wake disc of crab c; flag a fill when it meets another wall.
  const crossCool = [-999, -999, -999, -999];
  const paint = (c, tick) => {
    const bit = 1 << c.id, tp = tps[c.id];
    const Sc = S + tp.wakeLife;
    const wakeR = idiv(idiv(T.wakeR * tp.wakeMul, 1000) * R.bloom, 1000);
    forDisc(g, c.x, c.y, wakeR, i => {
      if (g.kind[i] !== OPEN || g.dark[i]) return;
      const thisTurn = g.turnOf[i] === S;
      const mine = thisTurn && (g.owner[i] & bit);
      if (mine && g.tick[i] > tick - T.selfGap) { g.tick[i] = tick; if (g.wake[i] < Sc) g.wake[i] = Sc; return; }   // our own fresh wake
      if ((!mine && g.wake[i] > 0 && g.wake[i] >= A) || (mine && g.tick[i] <= tick - T.selfGap)) {
        // Crossing a wake: someone else's, an older one, or our own earlier loop.
        lastFill.pending = true; lastFill.by = c.id;
        if ((g.owner[i] & ~bit || !thisTurn) && tick - crossCool[c.id] > T.crossCooldown) {
          crossCool[c.id] = tick;
          const [x, y] = centreOf(g, i);
          events.push({ type: 'cross', tick, crab: c.id, x, y, with: g.owner[i] & ~bit });
        }
      }
      if (!thisTurn) { g.owner[i] = 0; g.wake[i] = 0; }
      g.owner[i] |= bit;
      if (g.wake[i] < Sc) g.wake[i] = Sc;
      g.turnOf[i] = S;
      g.tick[i] = tick;
      if (!thisTurn && !lastFill.pending && touchesWall(g, i)) { lastFill.pending = true; lastFill.by = c.id; }
    });
  };

  // Currents carry everything that is moving: a position drift per tick
  // (integer accumulators, so small drifts are not lost to rounding).
  const cur = R.current;
  const drift = b => {
    if (!cur) return;
    const tp = b.type === 'crab' ? tps[b.id] : null;
    const k = b.type === 'little' ? 1200 : b.type === 'log' ? 500 : tp.anchor ? 0 : tp.mass >= 4 ? 600 : 1000;
    if (!k || (!b.vx && !b.vy)) return;
    b.cax = (b.cax || 0) + cur[0] * k * T.currentPush;
    b.cay = (b.cay || 0) + cur[1] * k * T.currentPush;
    const sx = idiv(b.cax, 1000000), sy = idiv(b.cay, 1000000);
    b.x += sx; b.y += sy; b.cax -= sx * 1000000; b.cay -= sy * 1000000;
  };

  recordFrame();
  let tick = 1;
  for (; tick <= T.maxTicks; tick++) {
    const movers = [...p.crabs, ...live().filter(l => targetOf(l).body), ...p.logs];
    if (!movers.some(b => b.vx || b.vy)) break;

    for (const b of movers) {
      const r = radiusOf(b);
      const px = b.x, py = b.y;
      drift(b);
      b.x += b.vx; b.y += b.vy;
      if (b.cool > 0) b.cool--;
      // Rim.
      for (const seg of segs) {
        const n = segmentContact(b, r, seg);
        if (!n) continue;
        const hit = bounce(b, n[0], n[1], b.type === 'little' ? T.littleRestitution : T.rimRestitution);
        if (hit > T.soundMin) events.push({ type: 'rim', tick, ...who(b), x: b.x, y: b.y, speed: hit, nx: n[0], ny: n[1] });
      }
      // Rocks, the boss, the rival crab.
      const solids = [...p.rocks];
      if (p.boss) solids.push(p.boss);
      if (p.rival) solids.push(p.rival);
      for (const o of solids) {
        const n = circleContact(b, r, o);
        if (!n) continue;
        const hit = bounce(b, n[0], n[1], T.rockRestitution);
        const kind = o === p.boss ? 'boss' : o === p.rival ? 'rivalHit' : 'rock';
        if (hit > T.soundMin) events.push({ type: kind, tick, ...who(b), x: b.x, y: b.y, speed: hit, nx: n[0], ny: n[1] });
        // A triton's horn makes the old one's water a little easier to fence.
        if (kind === 'boss' && b.type === 'crab' && tps[b.id].horn && hit > T.soundMin && p.bossBonus < T.hornMax) {
          p.bossBonus = Math.min(T.hornMax, p.bossBonus + T.hornStep);
          events.push({ type: 'horn', tick, crab: b.id, x: b.x, y: b.y, bonus: p.bossBonus });
        }
      }
      // Anemones hug crabs that run into them (unless the shell is spiny).
      for (const o of p.anemones) {
        const vin = b.vx * (o.x - b.x) + b.vy * (o.y - b.y);
        const n = circleContact(b, r, o);
        if (!n) continue;
        if (b.type === 'crab' && vin > 0 && !tps[b.id].spiny) {
          events.push({ type: 'hug', tick, crab: b.id, x: b.x, y: b.y, anemone: p.anemones.indexOf(o) });
          b.vx = 0; b.vy = 0; b.hugged = true;
        } else {
          const hit = bounce(b, n[0], n[1], T.rockRestitution);
          if (b.type === 'crab' && hit > T.soundMin) events.push({ type: 'spine', tick, crab: b.id, x: b.x, y: b.y, speed: hit, nx: n[0], ny: n[1] });
        }
      }
      // Whirlpools: in at one mouth, out of the other, moving the same way.
      if (!b.cool) {
        for (const w of p.vortices) {
          for (const [from, to] of [[w.a, w.b], [w.b, w.a]]) {
            if ((b.x - from.x) ** 2 + (b.y - from.y) ** 2 >= w.r * w.r) continue;
            const sp = len(b.vx, b.vy);
            const boost = b.type === 'crab' ? tps[b.id].vortexBoost : 1000;
            const out = Math.min(T.maxSpeed, idiv(Math.max(sp, T.minSpeed) * boost, 1000));
            const [dx, dy] = sp ? withLength(b.vx, b.vy, ONE) : [0, ONE];
            b.vx = idiv(dx * out, ONE); b.vy = idiv(dy * out, ONE);
            b.x = to.x + idiv(dx * (w.r + r), ONE); b.y = to.y + idiv(dy * (w.r + r), ONE);
            b.cool = 40;
            events.push({ type: 'vortex', tick, ...who(b), x: from.x, y: from.y, tx: to.x, ty: to.y });
            break;
          }
          if (b.cool) break;
        }
      }
      // Safety: never leave the pool.
      if (!insidePoly(p.poly, b.x, b.y)) { b.x = px; b.y = py; b.vx = -b.vx; b.vy = -b.vy; }
    }

    // Body against body.
    for (let i = 0; i < movers.length; i++) {
      for (let j = i + 1; j < movers.length; j++) collide(movers[i], movers[j], tick, events);
    }

    for (const b of movers) {
      // Damping: proportional drag plus a little constant friction so things settle.
      const tp = b.type === 'crab' ? tps[b.id] : null;
      const kelp = !(tp && tp.kelpGlide) && p.kelps.some(k => (b.x - k.x) ** 2 + (b.y - k.y) ** 2 < k.r * k.r);
      const keep = b.type === 'little' ? (b.kind === 'lantern' ? T.lanternKeep : b.kind === 'float' ? T.floatKeep : T.littleKeep) : b.type === 'log' ? T.logKeep : kelp ? T.kelpKeep : R.calm ? T.calmKeep : T.keep;
      b.vx = idiv(b.vx * keep, 4096); b.vy = idiv(b.vy * keep, 4096);
      const sp = len(b.vx, b.vy);
      if (sp <= T.stopSpeed) { b.vx = 0; b.vy = 0; continue; }
      [b.vx, b.vy] = withLength(b.vx, b.vy, sp - T.friction);
      if (sp > T.maxSpeed) [b.vx, b.vy] = withLength(b.vx, b.vy, T.maxSpeed);
    }

    for (const c of p.crabs) {
      if (!c.vx && !c.vy) continue;
      paint(c, tick);
      // Pearls on the night of pearls.
      const reach = T.crabR + T.pearlR + tps[c.id].pearlReach;
      for (const q of p.pearls) {
        if (q.taken || (c.x - q.x) ** 2 + (c.y - q.y) ** 2 >= reach * reach) continue;
        q.taken = true; p.stats.pearls++;
        events.push({ type: 'pearl', tick, crab: c.id, x: q.x, y: q.y, pearl: p.pearls.indexOf(q) });
      }
    }
    if (lastFill.pending && tick - lastFill.tick >= T.fillEvery) closePens(tick);
    // Saved by being pushed into lit water, reaching the outlet, or dropping into a net.
    for (const l of live()) {
      const save = targetOf(l).save;
      if (save === 'lit') {
        const i = cellAt(g, l.x, l.y);
        if (i >= 0 && g.kind[i] === LIT) saveLittle(l, tick, 'push');
      } else if (save === 'smallPen') {
        // A star already sitting in lit water (not scooped by a small pen) is lost in the light.
        const i = cellAt(g, l.x, l.y);
        if (i >= 0 && g.kind[i] === LIT) { l.state = 'faded'; events.push({ type: 'faded', tick, little: l.id, x: l.x, y: l.y }); }
      } else if (save === 'exit' && p.exit && (l.x - p.exit.x) ** 2 + (l.y - p.exit.y) ** 2 < p.exit.r * p.exit.r) {
        saveLittle(l, tick, 'exit');
      } else if (save === 'net') {
        const net = p.nets.find(n => (l.x - n.x) ** 2 + (l.y - n.y) ** 2 < n.r * n.r);
        if (net) saveLittle(l, tick, 'net');
      }
    }
    recordFrame();
  }
  if (lastFill.pending) closePens(tick);
  for (const b of [...p.crabs, ...p.littles, ...p.logs]) { b.vx = 0; b.vy = 0; b.cax = 0; b.cay = 0; }

  // Driftwood walls move to where the logs came to rest, which can close pens.
  if (p.logs.length) {
    placeLogs(p);
    closePens(tick);
  }

  // The undertow.
  const rings = spreadDark(g, A, spreadFor(p));
  events.push({ type: 'undertow', tick, rings });
  for (const l of live()) {
    const i = cellAt(g, l.x, l.y);
    // Glass floats ride ahead of the undertow: it shoves them to the nearest
    // water it has not reached (toward the shore nets). Only a float with no
    // such water left is carried out to sea.
    if (i >= 0 && g.dark[i] && l.kind === 'float') {
      const to = nearestClear(g, i);
      if (to >= 0) {
        const from = [l.x, l.y];
        l.x = g.x0 + (to % g.w) * CELL + CELL / 2; l.y = g.y0 + Math.floor(to / g.w) * CELL + CELL / 2;
        l.vx = 0; l.vy = 0;
        events.push({ type: 'shove', tick, little: l.id, from, x: l.x, y: l.y });
        const net = p.nets.find(n => (l.x - n.x) ** 2 + (l.y - n.y) ** 2 < n.r * n.r);
        if (net) { saveLittle(l, tick, 'net'); events[events.length - 1].late = true; }   // shown after the shove
        continue;
      }
    }
    if (i >= 0 && g.dark[i]) {
      l.state = 'taken';
      p.stats.taken++;
      events.push({ type: 'taken', tick, little: l.id, x: l.x, y: l.y });
    }
  }

  // The territorial crab dashes across the pool and cuts the wakes in its way.
  if (p.rival) rivalDash(p, tick, events);

  p.turn = S;
  return { pool: p, frames, events, ticks: tick };
}

// Along its axis (alternating each turn), toward the side where it can cut
// the most wake that would still block next turn.
function rivalDash(p, tick, events) {
  const g = p.grid, rv = p.rival;
  const nextA = p.rules.rain ? p.turn + 2 : p.turn + 1;
  const dirs = rv.axis === 0 ? [[ONE, 0], [-ONE, 0]] : [[0, ONE], [0, -ONE]];
  let best = null;
  for (const [dx, dy] of dirs) {
    const cut = new Set();
    let x = rv.x, y = rv.y;
    for (let k = 0; k < 200; k++) {
      const nx = x + idiv(dx * (CELL / 2), ONE), ny = y + idiv(dy * (CELL / 2), ONE);
      if (!insidePoly(p.poly, nx + idiv(dx * rv.r, ONE), ny + idiv(dy * rv.r, ONE))) break;
      x = nx; y = ny;
      forDisc(g, x, y, rv.r + CELL * 2, i => { if (g.wake[i] >= nextA) cut.add(i); });
    }
    if (!best || cut.size > best.cut.size) best = { cut, ex: x, ey: y };
  }
  // Wipe the wakes and settle at the far end. (The crab is a body to bounce
  // off, not a wall for pens.)
  for (const i of best.cut) { g.wake[i] = 0; g.owner[i] = 0; }
  events.push({ type: 'rival', tick, x: rv.x, y: rv.y, tx: best.ex, ty: best.ey, cut: [...best.cut] });
  rv.x = best.ex; rv.y = best.ey; rv.axis = 1 - rv.axis;
}

// Distance field to the sea outlet through open or lit water (wakes are walls),
// so lanterns follow the channels the crabs leave them.
// The nearest water cell (open or lit) the undertow has not reached, walking
// through water from cell i; -1 if the dark holds all of it.
function nearestClear(g, i) {
  const n = g.w * g.h, seen = new Uint8Array(n), q = [i];
  seen[i] = 1;
  for (let k = 0; k < q.length; k++) {
    const c = q[k], cx = c % g.w;
    if (!g.dark[c]) return c;
    for (const nb of [c + 1, c - 1, c + g.w, c - g.w]) {
      if (nb < 0 || nb >= n || seen[nb]) continue;
      if ((nb === c + 1 && cx === g.w - 1) || (nb === c - 1 && cx === 0)) continue;
      if (g.kind[nb] !== OPEN && g.kind[nb] !== LIT) continue;
      seen[nb] = 1; q.push(nb);
    }
  }
  return -1;
}

export function exitField(g, exit, A) {
  const n = g.w * g.h;
  const dist = new Int16Array(n).fill(-1);
  const q = [];
  forDisc(g, exit.x, exit.y, exit.r, i => { if (g.kind[i] === OPEN || g.kind[i] === LIT) { dist[i] = 0; q.push(i); } });
  for (let k = 0; k < q.length; k++) {
    const i = q[k], cx = i % g.w;
    for (const nb of [i + 1, i - 1, i + g.w, i - g.w]) {
      if (nb < 0 || nb >= n || dist[nb] >= 0) continue;
      if ((nb === i + 1 && cx === g.w - 1) || (nb === i - 1 && cx === 0)) continue;
      const kd = g.kind[nb];
      if ((kd !== OPEN && kd !== LIT) || (g.wake[nb] > 0 && g.wake[nb] >= A)) continue;
      dist[nb] = dist[i] + 1; q.push(nb);
    }
  }
  return dist;
}

function touchesWall(g, i) {
  const cx = i % g.w;
  for (const d of [1, -1, g.w, -g.w, g.w + 1, g.w - 1, -g.w + 1, -g.w - 1]) {
    const n = i + d;
    const nx = n % g.w;
    if (Math.abs(nx - cx) > 1) continue;
    const k = g.kind[n];
    if (k === OUT || k === SOLID || k === LIT) return true;
  }
  return false;
}

function collide(a, b, tick, events) {
  const ra = radiusOf(a), rb = radiusOf(b);
  let dx = b.x - a.x, dy = b.y - a.y;
  const rr = ra + rb;
  let d2 = dx * dx + dy * dy;
  if (d2 >= rr * rr) return;
  if (d2 === 0) { dx = 0; dy = 1; d2 = 1; }
  const d = isqrt(d2) || 1;
  const ma = massOf(a), mb = massOf(b);
  // Separate in proportion to the other body's mass.
  const overlap = rr - d;
  a.x -= idiv(dx * overlap * mb, d * (ma + mb)); a.y -= idiv(dy * overlap * mb, d * (ma + mb));
  b.x += idiv(dx * overlap * ma, d * (ma + mb)); b.y += idiv(dy * overlap * ma, d * (ma + mb));
  const k = (a.vx - b.vx) * dx + (a.vy - b.vy) * dy; // closing speed * d
  if (k <= 0) return;
  // Impulse for restitution e: j = (1+e) * k / (d^2 * (1/ma + 1/mb)).
  const e = TUNE.bodyRestitution;
  const ja = idiv(idiv(k * (1000 + e), 1000) * mb, ma + mb);
  const jb = idiv(idiv(k * (1000 + e), 1000) * ma, ma + mb);
  a.vx -= idiv(dx * ja, d2); a.vy -= idiv(dy * ja, d2);
  b.vx += idiv(dx * jb, d2); b.vy += idiv(dy * jb, d2);
  const speed = idiv(k, d);
  if (speed <= TUNE.soundMin) return;
  const types = [a.type, b.type].sort().join('-');
  const type = types === 'crab-crab' ? 'clack' : types === 'little-little' ? 'littles' : types.includes('log') ? 'thud' : 'bump';
  const crab = a.type === 'crab' ? a : b.type === 'crab' ? b : null;
  const little = a.type === 'little' ? a : b.type === 'little' ? b : null;
  const log = a.type === 'log' ? a : b.type === 'log' ? b : null;
  events.push({
    type, tick, speed, x: a.x + idiv(dx * ra, d), y: a.y + idiv(dy * ra, d),
    crab: crab?.id, other: type === 'clack' ? b.id : undefined, little: little?.id, log: log?.id,
  });
}
